import React, { useEffect, useState } from "react";
import { Dialog, Box, Typography, Button, TextField, CircularProgress, MenuItem } from "@mui/material";
import moment from "moment";
import { useSnackbar } from "notistack";
import { respond_to_booking_api } from "../../DAL/Tutors/Tutors";
import {
  refer_student_api, nudge_request_api, get_bookable_instructors_api,
} from "../../DAL/SchoolPortal/SchoolPortal";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  success, warning, error as errorColor, avatarColorFor, dialogPaperSx,
  primaryButtonSx, secondaryButtonSx, inputSx,
} from "../../layouts/schoolPortal/schoolPortalTheme";
import { useSchool } from "../../Hooks/SchoolContext";
import { regionOf, instructorNoun, anInstructor } from "../../utils/region";
import { prettyName } from "./StudentRecord/recordTheme";

/**
 * One shell, three decisions.
 *
 * A popup holds what you need to DECIDE; the record holds what you need to
 * understand. So the timeline, the transcripts, the sixteen lessons and the
 * approval queue are deliberately not here — five of forty timeline entries is
 * worse than a link to all of them.
 *
 *   Struggling  →  "do I intervene, and how?"
 *   Offered     →  "we already did. is it working, and when do I chase?"
 *   Requested   →  "accept or decline, before it expires"
 *   Booked      →  owner only: "is the money on this one sound?"
 *                  An instructor gets the real pre-session brief instead —
 *                  see WHY BOOKED LEAVES below.
 *   Studying    →  no popup. Nothing is pending, so clicking is browsing and
 *                  it navigates straight to the record.
 *
 * "DECIDE" IS NOT THE SAME WORD FOR EVERY ROLE
 * The first version of this file defined deciding from the CFI's chair —
 * accept, decline, am I ready to teach. For an owner or the front desk the
 * decision is "is this worth money, is it slipping, and who is already on it",
 * and that last part genuinely needs history. Acting twice on the same student,
 * or chasing one a colleague picked up yesterday, is the exact failure a shared
 * pipeline exists to prevent — and this popup could not see any of it.
 *
 * So one line of history is admitted, in the same place in every variant: the
 * contact strip. Everything else stays on the record.
 *
 * WHAT THE VIEWER CAN DO IS NOT A UI PREFERENCE
 * Three controllers scope on the caller being the assigned instructor:
 * respond_to_booking, propose_booking (which proposes the CALLER's diary), and
 * session_agenda. Worse, `operations` is not a tutor-capable role at all, so
 * the front desk could never propose anything — while Propose was their primary
 * button. Every action here therefore switches on role AND on `mine`, and where
 * something is not the viewer's to do they get a different real action rather
 * than a disabled button or a form that fails on submit.
 *
 * WHY BOOKED LEAVES
 * For an instructor a booked session is not a decision, it is a briefing —
 * read for five minutes before teaching, sometimes on a phone. SessionPrep
 * already renders it properly, with the agenda's own confidence and basis. This
 * file used to reimplement a truncated copy of it: three focus lines and the
 * last summary cut at 130 characters, in a 440px dialog. So a booked card
 * belonging to the viewer now opens the real brief, and only the commercial
 * question stays in the modal.
 */

const lbl = {
  fontFamily: font, fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em",
  textTransform: "uppercase", color: textSecondary,
};
const money = (n, c) =>
  new Intl.NumberFormat(c === "GBP" ? "en-GB" : "en-US",
    { style: "currency", currency: c || "USD", maximumFractionDigits: 0 }).format(n || 0);

// Same fallback as the board and the list: an absent health reads from the
// score, never as green.
const healthColour = (h, performance) => {
  if (h === "serious") return errorColor;
  if (h === "concern") return warning;
  if (h === "ok") return success;
  if (performance == null) return textMuted;
  return performance >= 85 ? success : performance >= 70 ? warning : errorColor;
};

/** Roles that can propose from their own diary. Mirrors TUTOR_CAPABLE_ROLES. */
const TUTOR_CAPABLE = ["instructor", "primary_admin", "staff_admin"];
const canTeach = (role) => TUTOR_CAPABLE.includes(role);

const daysBetween = (a, b) => Math.floor((new Date(a) - new Date(b)) / 86400000);

/**
 * Who has already touched this student, in one line, in the same place in every
 * variant. A count decides; the list explains, and the list is on the record.
 *
 * "Last emailed" says when we sent something and what it was. It does NOT say
 * it arrived: the path is nodemailer -> IONOS SMTP with no bounce, open or
 * click webhook, so `sent` means accepted by the SMTP server and nothing more
 * (models/email_log.js says so at length). Nothing here may imply otherwise.
 */
function ContactStrip({ s }) {
  const c = s.contact;
  if (!c) return null;
  const bits = [];
  if (s.lastActive) bits.push(`Last seen ${moment(s.lastActive).fromNow()}`);
  else bits.push("Never signed in");
  if (c.lastNoteAt) bits.push(`note ${moment(c.lastNoteAt).fromNow()}${c.lastNoteBy ? ` (${c.lastNoteBy})` : ""}`);
  if (c.openTasks) bits.push(`${c.openTasks} task${c.openTasks === 1 ? "" : "s"}${c.overdueTasks ? `, ${c.overdueTasks} overdue` : ""}`);
  if (c.lastEmailAt) bits.push(`emailed ${moment(c.lastEmailAt).fromNow()}`);
  if (!bits.length) return null;

  return (
    <Typography sx={{
      fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1.25,
      lineHeight: 1.45, borderLeft: `2px solid ${border}`, pl: 1,
    }}>
      {bits.join(" · ")}
      {c.overdueTasks > 0 && (
        <Box component="span" sx={{ color: warning, fontWeight: 700 }}> — overdue</Box>
      )}
    </Typography>
  );
}

function Header({ s, onOpenRecord }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
      <Box sx={{
        width: 34, height: 34, borderRadius: "9px", flexShrink: 0,
        backgroundColor: avatarColorFor(s.name), color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: font, fontWeight: 700, fontSize: "13px",
      }}>
        {s.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase()}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* The name IS the way to the record. A footer link treated the escape
            hatch as decoration and cost the footer a real action. */}
        <Typography
          onClick={onOpenRecord}
          sx={{
            fontFamily: font, fontSize: "15px", fontWeight: 800, color: textPrimary,
            cursor: "pointer", "&:hover": { color: accent, textDecoration: "underline" },
          }}
        >
          {s.name}
        </Typography>
        {s.progress && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.3 }}>
            <Box sx={{ height: 4, borderRadius: 2, backgroundColor: "#F0EDE9", flex: 1, maxWidth: 130, overflow: "hidden" }}>
              <Box sx={{ width: `${s.progress.completion}%`, height: "100%", backgroundColor: healthColour(s.health, s.progress.performance) }} />
            </Box>
            <Typography sx={{ fontFamily: font, fontSize: "11px", color: textMuted, fontVariantNumeric: "tabular-nums" }}>
              {s.progress.completion}% covered
              {s.progress.performance != null ? ` · ${s.progress.performance}% scoring` : ""}
            </Typography>
          </Box>
        )}
      </Box>
      {/* Ready for the written is a SALES TRIGGER in a revenue tool, not a
          status line. It was on the roster and rendered nowhere. */}
      {(s.written?.passed || s.written?.ready) && (
        <Box sx={{
          flexShrink: 0, px: 0.9, py: "3px", borderRadius: "999px",
          backgroundColor: s.written.passed ? `${success}18` : `${accent}14`,
          border: `1px solid ${s.written.passed ? success : accent}44`,
        }}>
          <Typography sx={{ fontFamily: font, fontSize: "10px", fontWeight: 800, color: s.written.passed ? success : accent }}>
            {s.written.passed ? "WRITTEN PASSED" : "READY FOR WRITTEN"}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/**
 * Hand this student to someone who can offer them a session.
 *
 * The front desk's move was never to offer a time — it has no diary. It is to
 * make sure someone who can offer one knows they should.
 */
function ReferForm({ student, targets, defaultReason, onDone, onCancel }) {
  const { enqueueSnackbar } = useSnackbar();
  const [who, setWho] = useState("");
  const [reason, setReason] = useState(defaultReason || "");
  const [busy, setBusy] = useState(false);

  // The list is the popup's now, not this form's — the buttons that open the
  // form have to know whether there is anyone to hand to before they render,
  // so fetching it here as well would be the same request twice.
  const rows = targets.rows;

  useEffect(() => {
    // Default to whoever already has them, so the common case is one press.
    const assigned = rows.find((i) => String(i.id) === String(student.nextSession?.instructorId || student.request?.instructorId));
    setWho(assigned ? assigned.id : rows[0]?.id || "");
  }, [rows, student.nextSession?.instructorId, student.request?.instructorId]);

  const send = async () => {
    if (!reason.trim()) {
      enqueueSnackbar("Say why — a referral with no reason is just a forwarded name", { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      const r = await refer_student_api(student.id, { instructorId: who, reason: reason.trim() });
      if (r.code === 200) { enqueueSnackbar(r.message, { variant: "success" }); onDone?.(); }
      else enqueueSnackbar(r.message || "That didn't go through", { variant: "error" });
    } finally { setBusy(false); }
  };

  if (targets.loading) return <CircularProgress size={16} sx={{ color: accent }} />;
  if (targets.error) return <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: errorColor }}>{targets.error}</Typography>;
  if (!rows.length) {
    return (
      <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, lineHeight: 1.5 }}>
        {targets.emptyMessage}
      </Typography>
    );
  }

  return (
    <>
      <TextField
        select size="small" fullWidth label="Hand it to" value={who}
        onChange={(e) => setWho(e.target.value)} sx={{ ...inputSx, mb: 1.25 }}
      >
        {rows.map((i) => (
          <MenuItem key={i.id} value={i.id}>
            {i.name}{i.bookable ? "" : " — not taking bookings"}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small" fullWidth multiline minRows={2} label="Why" value={reason}
        onChange={(e) => setReason(e.target.value)} sx={{ ...inputSx, mb: 1.25 }}
        placeholder="Stuck on weight and balance for three weeks — worth an hour with someone."
      />
      <Box sx={{ display: "flex", gap: 1 }}>
        <Button onClick={send} disabled={busy || !who} sx={{ ...primaryButtonSx, flex: 1 }}>
          {busy ? "Sending…" : "Send it"}
        </Button>
        <Button onClick={onCancel} sx={secondaryButtonSx}>Back</Button>
      </Box>
    </>
  );
}

export default function StudentPopup({
  student: s, open, onClose, onOpenRecord, onAction, viewerRole = null,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { school, isIndependentInstructor } = useSchool();
  const [busy, setBusy] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [referring, setReferring] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => { if (!open) { setDeclining(false); setReferring(false); setReason(""); } }, [open]);

  // Who this student could be handed to. Fetched by the popup rather than by
  // the form it feeds, because whether the referral action appears at all
  // depends on the answer: the endpoint excludes the caller, so an independent
  // instructor running a one-person group gets an empty list, and offering
  // "hand this to someone" to the only someone there is was the bug.
  const [targets, setTargets] = useState({ loading: true, rows: [], error: null });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setTargets({ loading: true, rows: [], error: null });
    get_bookable_instructors_api({ excludeSelf: true }).then((r) => {
      if (cancelled) return;
      if (r.code === 200) setTargets({ loading: false, rows: r.instructors || [], error: null });
      else setTargets({ loading: false, rows: [], error: r.message || "Could not load your instructors" });
    }).catch(() => {
      if (!cancelled) setTargets({ loading: false, rows: [], error: "Could not load your instructors" });
    });
    return () => { cancelled = true; };
  }, [open]);

  const region = regionOf(school);
  // Only offer a handover once we know there is a hand to give it to. Unknown
  // is treated as "no" so the button never appears and then vanishes.
  const canRefer = !targets.loading && !targets.error && targets.rows.length > 0;
  const referLabel = `Ask ${anInstructor(region)}`;
  const referTheirsLabel = `Ask their ${instructorNoun(region)}`;
  const emptyMessage = isIndependentInstructor
    ? "There is nobody else in your group to hand this to. Add a second instructor under Team first."
    : `Nobody else at your school is set up to teach yet. Add ${anInstructor(region)} under Staff first.`;

  if (!s) return null;

  const request = s.request;
  const session = s.nextSession;
  const isRequest = Boolean(request);
  const isBooked = !isRequest && s.stage === "booked" && Boolean(session);
  const mine = Boolean(request?.mine || session?.mine);
  const teaches = canTeach(viewerRole);

  const respond = async (decision) => {
    if (decision === "decline" && !reason.trim()) {
      enqueueSnackbar("Say why — an unexplained decline is what stops a student asking again", { variant: "warning" });
      return;
    }
    setBusy(true);
    try {
      const r = await respond_to_booking_api(request.id, decision);
      if (r.code === 200) {
        enqueueSnackbar(decision === "accept" ? "Accepted — it is in your diary" : "Declined, and the payment hold is released", { variant: "success" });
        onAction?.();
        onClose();
      } else enqueueSnackbar(r.message || "That didn't go through", { variant: "error" });
    } catch {
      enqueueSnackbar("That didn't go through", { variant: "error" });
    } finally { setBusy(false); }
  };

  const nudge = async () => {
    setBusy(true);
    try {
      const r = await nudge_request_api(s.id);
      enqueueSnackbar(r.message || "Nudged", { variant: r.code === 200 ? "success" : "error" });
      if (r.code === 200) onClose();
    } finally { setBusy(false); }
  };

  // Hours, not "in 6 hours" — under a day the unit is what makes it urgent.
  const hoursLeft = request?.expiresAt ? (new Date(request.expiresAt) - Date.now()) / 3600000 : null;
  const urgent = hoursLeft != null && hoursLeft < 12;
  const critical = hoursLeft != null && hoursLeft < 2;

  // Why the referral form does not start empty: the reason is already on screen.
  const referReason = s.struggle?.sentence || s.stageReason || "";

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { ...dialogPaperSx, maxWidth: 440 } }} fullWidth>
      <Box sx={{ p: 2.25 }}>
        <Header s={s} onOpenRecord={onOpenRecord} />
        <ContactStrip s={s} />

        {/* ── Requested ─────────────────────────────────────────────────── */}
        {isRequest && (
          <>
            {/* The clock leads. It is the reason this card outranks everything
                else on the board, and it used to be the fourth line inside a
                panel, under the date, the subject and the price. */}
            {hoursLeft != null && (
              <Box sx={{
                p: 1, borderRadius: "8px", mb: 1,
                backgroundColor: critical ? `${errorColor}12` : urgent ? `${warning}12` : "#F5F2EF",
                border: `1px solid ${critical ? errorColor : urgent ? warning : border}44`,
              }}>
                <Typography sx={{
                  fontFamily: font, fontSize: "13px", fontWeight: 800,
                  color: critical ? errorColor : urgent ? warning : textPrimary,
                }}>
                  {hoursLeft <= 0
                    ? "This has expired"
                    : hoursLeft < 24
                      ? `Expires in ${Math.max(1, Math.round(hoursLeft))} hour${Math.round(hoursLeft) === 1 ? "" : "s"}`
                      : `Expires ${moment(request.expiresAt).fromNow()}`}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 0.1 }}>
                  They are holding a payment on it.
                </Typography>
              </Box>
            )}

            <Box sx={{ p: 1.25, borderRadius: "8px", border: `1px solid ${border}`, mb: 1.25 }}>
              <Typography sx={lbl}>They asked you</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13.5px", fontWeight: 700, color: textPrimary, mt: 0.3 }}>
                {moment(request.at).format("ddd D MMM, HH:mm")}
                {request.subject ? ` · ${prettyName(request.subject)}` : ""}
              </Typography>
              {/* The amount, always: what the hour is worth is the fact you
                  act on. */}
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.2 }}>
                {money(request.price, request.currency)}
                {request.instructor ? ` · with ${request.instructor}` : ""}
              </Typography>
            </Box>

            {/* A student asking to pay you, in their words. Highest-converting
                thing on the screen; nothing goes above it but the clock. */}
            {request.note && (
              <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, fontStyle: "italic", mb: 1.25, lineHeight: 1.5 }}>
                “{request.note}”
              </Typography>
            )}

            {s.struggle && !request.note && (
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.25, lineHeight: 1.5 }}>
                {s.struggle.sentence}
              </Typography>
            )}

            {/* An accepted session on a dead card is a session that does not get
                paid for. This line existed only in the Struggling variant, so it
                was missing from the one screen where money changes hands. */}
            {s.value?.failedPayment && (
              <Typography sx={{
                fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: errorColor,
                mb: 1.25, lineHeight: 1.45,
              }}>
                Their card was declined on an earlier session — this one may not capture either.
              </Typography>
            )}

            {mine ? (
              declining ? (
                <>
                  <TextField
                    label="Why not?" size="small" fullWidth multiline minRows={2} autoFocus
                    value={reason} onChange={(e) => setReason(e.target.value)} sx={{ ...inputSx, mb: 1.5 }}
                    placeholder="I'm away that week — try Tuesday the 9th?"
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={() => respond("decline")} disabled={busy} sx={{ ...primaryButtonSx, flex: 1 }}>
                      {busy ? "Declining…" : "Decline"}
                    </Button>
                    <Button onClick={() => setDeclining(false)} sx={secondaryButtonSx}>Back</Button>
                  </Box>
                </>
              ) : (
                <>
                  {/* Moved out of the decline branch. Uncertainty about money is
                      what stops someone pressing Accept, so it has to be visible
                      BEFORE they choose, not after. */}
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textMuted, mb: 1, lineHeight: 1.45 }}>
                    Nothing is charged either way. Declining releases the hold — it never shows on their statement.
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={() => respond("accept")} disabled={busy} sx={{ ...primaryButtonSx, flex: 1 }}>
                      {busy ? "Accepting…" : "Accept"}
                    </Button>
                    <Button onClick={() => setDeclining(true)} sx={secondaryButtonSx}>Decline</Button>
                  </Box>
                </>
              )
            ) : (
              // Not theirs to answer — respond_to_booking scopes on the assigned
              // instructor, so a button here would 404. But "waiting on Dana" as
              // the only content made the viewer a spectator on the one card
              // where the school can still lose money it already had.
              <>
                <Box sx={{ p: 1.25, borderRadius: "8px", backgroundColor: "#F5F2EF", mb: 1.25 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textPrimary }}>
                    Waiting on {request.instructor || "their instructor"} to answer.
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 0.2 }}>
                    Only the instructor it was sent to can accept or decline it.
                  </Typography>
                </Box>
                <Button onClick={nudge} disabled={busy} sx={{ ...primaryButtonSx, width: "100%" }}>
                  {busy ? "Sending…" : `Nudge ${request.instructor?.split(" ")[0] || "them"}`}
                </Button>
              </>
            )}
          </>
        )}

        {/* ── Booked, seen by someone who is not teaching it ─────────────── */}
        {isBooked && (
          <>
            <Box sx={{ p: 1.25, borderRadius: "8px", border: `1px solid ${border}`, mb: 1.25 }}>
              <Typography sx={lbl}>Next session</Typography>
              <Typography sx={{ fontFamily: font, fontSize: "13.5px", fontWeight: 700, color: textPrimary, mt: 0.3 }}>
                {moment(session.at).format("ddd D MMM, HH:mm")}
                {session.subject ? ` · ${prettyName(session.subject)}` : ""}
              </Typography>
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, mt: 0.2 }}>
                {money(session.price, session.currency)}
                {session.instructor ? ` · with ${session.instructor}` : ""}
              </Typography>
            </Box>

            {/* Lifetime revenue answered nothing — nobody opens a booked card to
                learn what a student has spent since March. The question is
                whether THIS session's money is sound. */}
            {s.value?.failedPayment ? (
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: errorColor, mb: 1.25, lineHeight: 1.45 }}>
                Their card was declined on an earlier session. Sort it before this one runs.
              </Typography>
            ) : (
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.25, lineHeight: 1.45 }}>
                Payment is in order.
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button onClick={() => onAction?.("note", s)} sx={{ ...primaryButtonSx, flex: 1, minWidth: 120 }}>
                Add note
              </Button>
              {session.instructorId && canRefer && (
                <Button onClick={() => setReferring(true)} sx={secondaryButtonSx}>{referTheirsLabel}</Button>
              )}
            </Box>
          </>
        )}

        {/* ── Struggling and Offered ────────────────────────────────────── */}
        {!isRequest && !isBooked && (
          <>
            {/* Offered is not Struggling with a footnote. The decision is not
                "should we offer" — we did — it is "is it working, and when do I
                chase". So the offer leads and the struggle is demoted. */}
            {s.stage === "offered" && (
              <Box sx={{ p: 1.25, borderRadius: "8px", border: `1px solid ${accent}44`, background: `${accent}0a`, mb: 1.25 }}>
                <Typography sx={{ ...lbl, color: accent }}>Offered {moment(s.stageAt).fromNow()}</Typography>
                <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, mt: 0.3, lineHeight: 1.5 }}>
                  {s.stageReason}
                </Typography>
                {/* Two different problems wearing the same badge, told apart by
                    two fields already on the wire. Have they seen it and said
                    nothing, or have they not been back at all? One is chase the
                    offer; the other is chase the person. */}
                <Typography sx={{ fontFamily: font, fontSize: "12px", fontWeight: 700, color: warning, mt: 0.5, lineHeight: 1.45 }}>
                  {!s.lastActive
                    ? "They have never signed in, so they have not seen it."
                    : new Date(s.lastActive) > new Date(s.stageAt)
                      ? `They have signed in since (${moment(s.lastActive).fromNow()}) and not answered.`
                      : `They have not signed in since — last seen ${moment(s.lastActive).fromNow()}.`}
                </Typography>
              </Box>
            )}

            {s.struggle && (
              <Box sx={{
                p: 1.25, borderRadius: "8px", mb: 1.25,
                border: `1px solid ${s.struggle.colour}44`,
                background: s.stage === "offered" ? "transparent" : `${s.struggle.colour}0a`,
              }}>
                <Typography sx={{ ...lbl, color: s.struggle.colour }}>{s.struggle.label}</Typography>
                <Typography sx={{ fontFamily: font, fontSize: "13px", color: textPrimary, mt: 0.3, lineHeight: 1.5 }}>
                  {s.struggle.sentence}
                </Typography>
                <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mt: 0.4 }}>
                  Whose call: {s.struggle.actor}
                  {/* One line, not three sentences. The leading kind already
                      chose the actor, so the others change nothing about what
                      you do next — they belong on the record. */}
                  {s.struggle.others > 0 && ` · also ${s.struggle.all.slice(1).map((k) => k.label.toLowerCase()).join(", ")}`}
                </Typography>
              </Box>
            )}

            {/* A declined card is no longer a KIND of struggle — struggling is
                about learning, and a bounced card is front-desk work. So it can
                no longer be both the panel's headline and a sentence under it:
                it is only ever the sentence. */}
            {s.value?.failedPayment && (
              <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 700, color: errorColor, mb: 1.25, lineHeight: 1.45 }}>
                Their card has been declined — sort that before offering anything.
              </Typography>
            )}

            {s.inGrace ? (
              // Grace has to change the ACTION, not sit above a button urging
              // the opposite. "Signals are held to see if it worked" directly
              // over "Propose a session" was the screen arguing with itself.
              <>
                <Typography sx={{ fontFamily: font, fontSize: "12.5px", color: textSecondary, mb: 1.25, lineHeight: 1.5 }}>
                  They had a session {daysBetween(new Date(), s.stageAt) >= 0 ? `${moment(s.stageAt).fromNow()}` : "recently"}.
                  Signals are held until {moment(s.graceEndsAt).format("D MMM")} to see whether it worked — booking another
                  now tells you nothing about the last one.
                </Typography>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button onClick={() => onAction?.("note", s)} sx={{ ...primaryButtonSx, flex: 1, minWidth: 120 }}>
                    Add note
                  </Button>
                  <Button onClick={() => onAction?.("task", s)} sx={secondaryButtonSx}>Set a task</Button>
                </Box>
              </>
            ) : referring ? (
              <ReferForm
                student={s}
                targets={{ ...targets, emptyMessage }}
                defaultReason={referReason}
                onDone={() => { setReferring(false); onAction?.(); onClose(); }}
                onCancel={() => setReferring(false)}
              />
            ) : (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {teaches ? (
                  <>
                    {/* Offering again to someone who already has an open offer
                        is duplicate outreach on the one screen built to stop it. */}
                    <Button
                      onClick={() => onAction?.(s.stage === "offered" ? "note" : "propose", s)}
                      sx={{ ...primaryButtonSx, flex: 1, minWidth: 130 }}
                    >
                      {s.stage === "offered" ? "Chase it" : "Propose a session"}
                    </Button>
                    {canRefer && (
                      <Button onClick={() => setReferring(true)} sx={secondaryButtonSx}>{referLabel}</Button>
                    )}
                  </>
                ) : (
                  <>
                    {/* propose_booking offers times from the CALLER's diary and
                        `operations` is not even a tutor-capable role, so Propose
                        could never have worked here. Handing it to someone who
                        can is the real action — where there is someone to hand
                        to. In a one-person group there is not, and a task is
                        the only move left. */}
                    {canRefer && (
                      <Button onClick={() => setReferring(true)} sx={{ ...primaryButtonSx, flex: 1, minWidth: 130 }}>
                        {referLabel}
                      </Button>
                    )}
                    <Button
                      onClick={() => onAction?.("task", s)}
                      sx={canRefer ? secondaryButtonSx : { ...primaryButtonSx, flex: 1, minWidth: 130 }}
                    >
                      Set a task
                    </Button>
                  </>
                )}
              </Box>
            )}
          </>
        )}

        {referring && (isRequest || isBooked) && (
          <Box sx={{ mt: 1.25 }}>
            <ReferForm
              student={s}
              targets={{ ...targets, emptyMessage }}
              defaultReason={referReason}
              onDone={() => { setReferring(false); onAction?.(); onClose(); }}
              onCancel={() => setReferring(false)}
            />
          </Box>
        )}

        <Box sx={{ display: "flex", alignItems: "center", mt: 1.75, pt: 1.25, borderTop: `1px solid ${border}` }}>
          <Button onClick={onOpenRecord} sx={{
            textTransform: "none", fontFamily: font, fontSize: "12.5px", fontWeight: 700,
            color: accent, p: 0, minWidth: 0, "&:hover": { background: "none", textDecoration: "underline" },
          }}>
            Open full record →
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} sx={{ ...secondaryButtonSx, minHeight: 0, py: 0.5, px: 1.5, fontSize: "12.5px" }}>
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}
